const BASE = "/api";

async function request(path, options = {}) {
  const res = await fetch(`${BASE}${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    ...options,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.error || `Request failed (${res.status})`);
  }
  return data;
}

export const api = {
  // auth
  login: (username, password) =>
    request("/auth/login", { method: "POST", body: JSON.stringify({ username, password }) }),
  logout: () => request("/auth/logout", { method: "POST" }),
  session: () => request("/auth/session"),
  register: (payload) =>
    request("/auth/register", { method: "POST", body: JSON.stringify(payload) }),

  // users
  listUsers: () => request("/users"),
  deleteUser: (username) => request(`/users/${username}`, { method: "DELETE" }),

  // broker
  brokerConfig: () => request("/broker/config"),
  brokerConnect: (payload) =>
    request("/broker/connect", { method: "POST", body: JSON.stringify(payload) }),
  brokerQuickConnect: (password_or_pin) =>
    request("/broker/quick-connect", { method: "POST", body: JSON.stringify({ password_or_pin }) }),
  brokerDisconnect: () => request("/broker/disconnect", { method: "POST" }),
  brokerForget: () => request("/broker/config", { method: "DELETE" }),
  brokerPositions: () => request("/broker/positions"),

  // strategies
  listStrategies: (segment) =>
    request(segment ? `/strategies?segment=${encodeURIComponent(segment)}` : "/strategies"),
  getStrategy: (id) => request(`/strategies/${id}`),
  createStrategy: (config) =>
    request("/strategies", { method: "POST", body: JSON.stringify(config) }),
  updateStrategy: (id, config) =>
    request(`/strategies/${id}`, { method: "PUT", body: JSON.stringify(config) }),
  deleteStrategy: (id) => request(`/strategies/${id}`, { method: "DELETE" }),

  // strategy execution engine
  startStrategy: (id, dryRun) =>
    request(`/strategies/${id}/start`, { method: "POST", body: JSON.stringify({ dry_run: dryRun }) }),
  stopStrategy: (id) => request(`/strategies/${id}/stop`, { method: "POST" }),
  strategyStatus: (id) => request(`/strategies/${id}/status`),
  strategyLogs: (id) => request(`/strategies/${id}/logs`),
  strategyQuote: (id) => request(`/strategies/${id}/quote`),

  // market data
  getAtmInfo: (underlying, segment) =>
    request(`/market/atm${toQuery({ underlying, segment })}`),
  getOptionChain: (underlying, segment) =>
    request(`/market/option-chain${toQuery({ underlying, segment })}`),

  // alerts (email / Telegram / SMS on strategy entry/exit)
  getNotificationConfig: () => request("/notifications/config"),
  saveNotificationConfig: (cfg) =>
    request("/notifications/config", { method: "POST", body: JSON.stringify(cfg) }),
  testNotification: () => request("/notifications/test", { method: "POST" }),

  // trade history / P&L
  listTrades: (params = {}) => request(`/trades${toQuery(params)}`),
  tradeSummary: (params = {}) => request(`/trades/summary${toQuery(params)}`),

  // Export returns a file, not JSON -- can't reuse request() (it always
  // calls res.json()). Pulls the filename Flask set via Content-Disposition
  // so the browser's save dialog shows the real name instead of a blob URL.
  exportTrades: async (params = {}) => {
    const res = await fetch(`${BASE}/trades/export${toQuery(params)}`, { credentials: "include" });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error || `Export failed (${res.status})`);
    }
    const blob = await res.blob();
    const disposition = res.headers.get("Content-Disposition") || "";
    const match = disposition.match(/filename="?([^"]+)"?/);
    return { blob, filename: match ? match[1] : "trades_export.xlsx" };
  },
};

function toQuery(params) {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "");
  if (entries.length === 0) return "";
  const qs = entries.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join("&");
  return `?${qs}`;
}
